import { Component, EventEmitter, Input, OnInit, Output, Renderer2, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgIf, NgFor, UpperCasePipe } from '@angular/common';
import { IconComponent } from '../general/icon/icon.component';
import { Language, LanguageResponse, LanguageSubjectModel } from './language-switcher.model';
import { OverlayContainer } from '@angular/cdk/overlay';
import { LanguageSwitcherService } from './language-switcher.service';
import { CacheService } from '../../services/cache.service';
import { CACHE_KEYS } from '../../constants/cache-keys';
import { Router } from '@angular/router';
import { addFragmentAndReload, getBrowserLanguage } from '../../util/utils';
import { PageService } from '../../services/page/page.service';
import { LoginResults } from '../../../login/login.viewmodel';
@Component({
  selector: 'nuc-language-switcher',
  imports: [FormsModule, ReactiveFormsModule, MatSelectModule, NgIf, NgFor, UpperCasePipe, IconComponent],
  templateUrl: './language-switcher.component.html',
  styleUrl: './language-switcher.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LanguageSwitcherComponent implements OnInit {
  @Input() options: any[];
  @Input() displayKey: string;
  @Input() label: string = '';
  @Input() icon: string = '';
  @Input() formControl: FormControl | null = null;
  @Input() errorMessage: string = 'Invalid selection';
  @Input() displayClass: string = '';
  @Input() name: string;
  @Input() prefixIcon: string = '';
  @Input() displayColor: string;
  @Input() iconClass: string;
  @Input() size: string; 
  @Input() displayIconClass: string = '';
  @Input() trackByKey: string;
  value: string = '';
  optionType: 'primitive' | 'object' = 'primitive';
  selectedLanguage: Language;
  authoredLanguages: Language[] = [];
  languages: Language[] = [];
  prefLanguage: Language;
  userType: string;
  loginData?: LoginResults;
  @Input() isDisabled: boolean = false;
  @ViewChild(MatSelect) matSelect!: MatSelect;
  @Output() selectionChange = new EventEmitter<any>();

  constructor(private router: Router, private pageService: PageService, private overlayContainer: OverlayContainer, private languageService: LanguageSwitcherService, private cacheService: CacheService, private renderer: Renderer2) { }

  ngOnInit(): void {
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    this.setOptionType();
    if (this.formControl && this.isDisabled) {
      this.formControl.disable();
    }
    this.languageService.languageStatus$.subscribe((langModel: LanguageSubjectModel) => {
      if (langModel?.status == "true") {
        this.setInitLanguage(langModel.authoredLanguages);
      }
    });
  }

  ngAfterViewInit() {
    this.matSelect.openedChange.subscribe((opened: boolean) => {
      const overlayContainerElement = this.overlayContainer.getContainerElement();
      if (opened) {
        this.renderer.addClass(overlayContainerElement, 'language-switcher-options');
      } else {
        this.renderer.removeClass(overlayContainerElement, 'language-switcher-options');
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loginData = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LOGIN_DATA);
    this.userType = this.loginData?.userType;
    if (changes['options']) {
      this.setOptionType();
    }
    if (changes['isDisabled'] && this.formControl) {
      changes['isDisabled'].currentValue ? this.formControl.disable() : this.formControl.enable();
    }
  }

  private setOptionType() {
    if (this.options && this.options.length > 0) {
      this.optionType = typeof this.options[0] === 'object' ? 'object' : 'primitive';
    } else {
      this.optionType = 'primitive';
    }
  }

  onLanguageSelectionChange(event: any): void {
    this.selectedLanguage = event.value;
    this.commitLanguageAndReload();
  }

  trackLanguagesBy(index: number, item: Language): string {
    return `${item.languageCode}-${item.countryCode}`;
  }

  onSelectionChange(event: MatSelectChange) {
    this.selectionChange.emit(event.value);
    // If you want this component to act as a full CVA for template-driven usage:
    this.onChange(event.value);
    this.onTouched();
  }

  // Optional: proper compare for object values
  compareWith = (a: any, b: any) => {
    if (!a || !b) return a === b;
    // Prefer trackByKey if provided, else fall back to common keys
    if (this.trackByKey && a[this.trackByKey] && b[this.trackByKey]) {
      return a[this.trackByKey] === b[this.trackByKey];
    }
    if ('isoCode' in a && 'isoCode' in b) return a.isoCode === b.isoCode;
    if ('languageCode' in a && 'languageCode' in b) return a.languageCode === b.languageCode;
    return JSON.stringify(a) === JSON.stringify(b);
  };



  private setInitLanguage(authoredLanguages: Language[]) {
    this.prefLanguage = this.pageService.getPreferredLanguage(); //get the preferred lang either from cache or from browser
    const defaultLang: Language = { countryCode: 'US', languageCode: 'en' };
    const browserLang: Language = getBrowserLanguage();
    if (authoredLanguages.some(x => x.languageCode == browserLang.languageCode)) {
      this.languages.push(browserLang);
    }
    browserLang.languageCode != "en" && this.languages.push(defaultLang);
    this.selectedLanguage = this.prefLanguage.languageCode == browserLang.languageCode ? browserLang : defaultLang;
    this.commitLanguageAndReload();
  }

  private commitLanguageAndReload() {
    const prevLanguage = this.cacheService.getFromCache(CACHE_KEYS.LOGIN.LANGUAGE);
    this.cacheService.addToCache(CACHE_KEYS.LOGIN.LANGUAGE, this.selectedLanguage);
    if (prevLanguage && this.selectedLanguage.languageCode != prevLanguage?.languageCode) {
      addFragmentAndReload();
      // this.router.resetConfig(this.router.config);
      // this.router.navigate([this.router.url]);
    }
  }

   // CVA plumbing
  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  trackByFunction(index: number, option: any): any {
    return this.trackByKey ? option[this.trackByKey] : index;
  }
}